import * as React from 'react';
import {Dispatch, useState} from "react";
import {Autocomplete, CircularProgress, TextField} from "@mui/material";
import {MobileDatePicker} from "@mui/x-date-pickers/MobileDatePicker";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFnsV3";
import {addDays, format, parse} from "date-fns";
import {lt} from "date-fns/locale";
import {NotPossibleToDeliver} from "./Helper/NotPossibleToDeliver";

type DeliveryModel = {
    setStep: Dispatch<any>,
    address: string,
    setAddress: Dispatch<any>,
    deliveryDate: string,
    setDeliveryDate: Dispatch<any>,
    deliveryTime: number,
    setDeliveryTime: Dispatch<any>,
    notes: string
    setNotes: Dispatch<any>,
    setDeliveryTimeLabel: Dispatch<any>,
    apiUrl: string,
    stepsBarRef: any,
    selectedCity: string,
    selectedSize: string,
    floor: number,
    bagsCount: number,
    elevatorExist: boolean,
    trashNearHouse: boolean
};


interface Address {
    address: string;
}

interface TimeInterval {
    timeInterval: string;
    label: string;
    icon: string;
    timeIntervalId: number;
}

interface Floor {
    label: string;
    description: string;
    price: string;
    floorId: number;
    icon: string;
}

interface Price {
    totalPrice: number,
    deliveryTodayPrice: number,
    floorPrice: number,
    garbagePackagePrice: number,
    bagsPrice: number
}

interface AvailabilityDay {
    date: string;
    available: boolean;
    timeIntervalIds: number[];
}

function fetchFloors(apiUrl: string, signal: AbortSignal) {
    return new Promise<{
        timeIntervals: Floor[]
    }>((resolve, reject) => {
        const options = {
            method: 'GET',
            headers: {
                'X-Requested-With': 'XMLHttpRequest'
            },
            signal
        };
        fetch(apiUrl + 'floors', options)
            .then((res) => {
                if (!res.ok) {
                    throw new Error(`HTTP error! status: ${res.status}`);
                }
                return res.json();
            })
            .then((timeIntervals) => {
                resolve({timeIntervals});
            })
            .catch(reject);
    });
}

function fetchPrice(apiUrl: string, city: string, size: string, deliveryDate: string, floor: number, bagsCount: number, signal: AbortSignal) {
    return new Promise<{
        finalPrice: Price
    }>((resolve, reject) => {
        const options = {
            method: 'GET',
            headers: {
                'X-Requested-With': 'XMLHttpRequest'
            },
            signal
        };
        fetch(apiUrl + 'price/' + encodeURIComponent(city) + '/' + encodeURIComponent(size) + '/' + encodeURIComponent(deliveryDate) + '/' + floor + '/' + bagsCount, options)
            .then((res) => {
                if (!res.ok) {
                    throw new Error(`HTTP error! status: ${res.status}`);
                }
                return res.json();
            })
            .then((finalPrice) => {
                resolve({finalPrice});
            })
            .catch(reject);
    });
}

function fetchAddress(apiUrl: string, textInput: string, city: string, signal: AbortSignal) {
    return new Promise<{
        options: Address[],
    }>((resolve, reject) => {

        const params = {
            textInput: textInput,
            city: city,
        };
        const options = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(params),
            signal
        };

        fetch(apiUrl + 'address-autocomplete', options)
            .then((res) => {
                if (!res.ok) {
                    throw new Error(`HTTP error! status: ${res.status}`);
                }
                return res.json();
            })
            .then(({options}) => {
                resolve({options});
            })
            .catch(reject);
    });
}


function parseTimeIntervalRange(timeInterval: string, baseDate: Date): { start: Date; end: Date } | null {
    const match = timeInterval.match(/(\d{1,2}):(\d{2})\s*-\s*(\d{1,2}):(\d{2})/);

    if (!match) {
        return null;
    }

    const [, startHour, startMinute, endHour, endMinute] = match;

    const start = new Date(baseDate);
    start.setHours(parseInt(startHour, 10), parseInt(startMinute, 10), 0, 0);

    const end = new Date(baseDate);
    end.setHours(parseInt(endHour, 10), parseInt(endMinute, 10), 0, 0);

    return {start, end};
}

// Once a slot's end time has passed, it can no longer be booked for today —
// e.g. once it's past 12:00, "Rytas" (09:00-12:00) drops out, leaving only
// the slots that haven't ended yet. Other dates are unaffected.
function isTimeSlotAvailableToday(item: TimeInterval): boolean {
    const range = parseTimeIntervalRange(item.timeInterval, new Date());

    if (!range) {
        return true;
    }

    return range.end.getTime() > Date.now();
}

function fetchDriverAvailability(apiUrl: string, city: string, signal: AbortSignal) {
    return new Promise<{
        days: AvailabilityDay[]
    }>((resolve, reject) => {
        const options = {
            method: 'GET',
            headers: {
                'X-Requested-With': 'XMLHttpRequest'
            },
            signal
        };
        fetch(apiUrl + 'delivery-availability/' + encodeURIComponent(city), options)
            .then((res) => {
                if (!res.ok) {
                    throw new Error(`HTTP error! status: ${res.status}`);
                }
                return res.json();
            })
            .then((data) => {
                resolve({days: data.days ?? []});
            })
            .catch(reject);
    });
}

function fetchDeliveryTime(apiUrl: string, signal: AbortSignal) {
    return new Promise<{
        timeIntervals: TimeInterval[]
    }>((resolve, reject) => {
        const options = {
            method: 'GET',
            headers: {
                'X-Requested-With': 'XMLHttpRequest'
            },
            signal
        };
        fetch(apiUrl + 'time-intervals', options)
            .then((res) => {
                if (!res.ok) {
                    throw new Error(`HTTP error! status: ${res.status}`);
                }
                return res.json();
            })
            .then((timeIntervals) => {
                resolve({timeIntervals});
            })
            .catch(reject);
    });
}

export const DeliveryInformationStep = ({
                                            setStep,
                                            address,
                                            setAddress,
                                            deliveryDate,
                                            setDeliveryDate,
                                            deliveryTime,
                                            setDeliveryTime,
                                            notes,
                                            setNotes,
                                            setDeliveryTimeLabel,
                                            apiUrl,
                                            stepsBarRef,
                                            selectedCity,
                                            selectedSize,
                                            floor,
                                            bagsCount,
                                            elevatorExist,
                                            trashNearHouse
                                        }: DeliveryModel) => {

    const [deliveryDateError, setDeliveryDateError] = useState(false);
    const [deliveryTimeError, setDeliveryTimeError] = useState(false);
    const [showNotPossibleToDeliver, setShowNotPossibleToDeliver] = useState(false);
    const [addressError, setAddressError] = useState(false);
    const requestAbortController = React.useRef<AbortController | null>(null);
    const priceAbortController = React.useRef<AbortController | null>(null);
    const floorsAbortController = React.useRef<AbortController | null>(null);
    const [open, setOpen] = React.useState(false);
    const [options, setOptions] = React.useState<readonly Address[]>([]);
    const loading = open && options.length === 0;
    const [value, setValue] = React.useState<Address | null>({address: address});
    const [inputValue, setInputValue] = React.useState(address);
    const [deliveryTimeList, setDeliveryTimeList] = useState([]);
    const [finalPrice, setFinalPrice] = useState<Price | null>(null);
    const [floorsList, setFloorsList] = useState<Floor[]>([]);
    const [datePickerOpen, setDatePickerOpen] = useState(false);
    const [availabilityByDate, setAvailabilityByDate] = useState<Record<string, AvailabilityDay>>({});
    const availabilityAbortController = React.useRef<AbortController | null>(null);

    const selectedFloor = floorsList.find((item) => item.floorId === floor);

    const todayDateKey = format(new Date(), 'yyyy-MM-dd');
    const tomorrowDateKey = format(addDays(new Date(), 1), 'yyyy-MM-dd');

    const selectedDateKey = deliveryDate === 'today'
        ? todayDateKey
        : deliveryDate === 'tomorrow'
            ? tomorrowDateKey
            : deliveryDate;

    const dayAvailability = availabilityByDate[selectedDateKey];
    const todayAvailability = availabilityByDate[todayDateKey];
    const tomorrowAvailability = availabilityByDate[tomorrowDateKey];

    // availabilityByDate only knows about dates the backend has actually returned
    // data for (see fetchDriverAvailability effect below) — until it does, or for
    // a city with no data at all, fail open rather than blocking selection.
    const isTodayDisabled = todayAvailability ? !todayAvailability.available : false;
    const isTomorrowDisabled = tomorrowAvailability ? !tomorrowAvailability.available : false;

    // A slot is only selectable once it hasn't ended yet (today only) AND at
    // least one driver assigned to the selected city actually covers it.
    const isTimeSlotDisabled = (item: TimeInterval): boolean => {
        if (deliveryDate === 'today' && !isTimeSlotAvailableToday(item)) {
            return true;
        }

        if (dayAvailability && !dayAvailability.timeIntervalIds.includes(item.timeIntervalId)) {
            return true;
        }

        return false;
    };

    const noSlotsAvailable = deliveryDate !== ''
        && deliveryTimeList.length > 0
        && deliveryTimeList.every(isTimeSlotDisabled);

    const customDateValue = (deliveryDate !== '' && deliveryDate !== 'today' && deliveryDate !== 'tomorrow')
        ? parse(deliveryDate, 'yyyy-MM-dd', new Date())
        : null;

    const handleCustomDateChange = (value: Date | null) => {
        if (value) {
            setDeliveryDate(format(value, 'yyyy-MM-dd'));
            setDeliveryDateError(false);
        }
    };

    const fetchTimeIntervals = () => {
        const controller = new AbortController();
        fetchDeliveryTime(apiUrl, controller.signal)
            .then(({timeIntervals}) => {
                setDeliveryTimeList(timeIntervals);
            })
            .catch((error) => {
                if (error.name !== 'AbortError') {
                    console.error(error);
                }
            });

        requestAbortController.current = controller;
    };

    const fetchAddressOptions = (textInput: string) => {
        const controller = new AbortController();
        fetchAddress(apiUrl, textInput, selectedCity, controller.signal)
            .then(({options}) => {
                setOptions([...options]);
            })
            .catch((error) => {
                if (error.name !== 'AbortError') {
                    console.error(error);
                }
            });

        requestAbortController.current = controller;
    };

    const handleNextClick = () => {
        let valid: boolean = true;

        if (deliveryDate === '') {
            valid = false;
            setDeliveryDateError(true);
        } else {
            setDeliveryDateError(false);
        }

        if (deliveryTime === 0) {
            valid = false;
            setDeliveryTimeError(true);
        } else {
            setDeliveryTimeError(false);
        }

        if (!address || address.length == 0) {
            valid = false;
            setAddressError(true);
        } else {
            setAddressError(false);
        }

        if (valid) {
            setStep(4);
        }
        stepsBarRef.current?.scrollIntoView({behavior: 'smooth'});
    };

    const handleBackClick = () => {
        setStep(2);
    };

    const handleTimeSelection = (event: any) => {
        const selected = deliveryTimeList.find(item => item.timeIntervalId == event.target.value);

        setDeliveryTime(parseInt(event.target.value));
        if (selected) {
            setDeliveryTimeLabel(selected.timeInterval);
        }
        setDeliveryTimeError(false);
    };

    const handleNotesFill = (event: any) => {
        setNotes(event.target.value)
    };

    const handleDateSelection = (event: any) => {
        const dateSetValue = event.currentTarget.dataset.value;

        if (dateSetValue !== undefined) {
            setDeliveryDate(dateSetValue);
            setDeliveryDateError(false);
        }
    };

    const handleDateSelectionKeyDown = (event: any) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleDateSelection(event);
        }
    };

    React.useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            let active = true;

            if (active && address.length > 3) {
                fetchAddressOptions(address);
            }

            return () => {
                active = false;
            };
            // Send Axios request here
        }, 700)

        return () => clearTimeout(delayDebounceFn)
    }, [value, address]);

    React.useEffect(() => {
        fetchTimeIntervals();
        // abort request on unmount
        return () => requestAbortController.current?.abort();
    }, []);

    React.useEffect(() => {
        const controller = new AbortController();
        fetchFloors(apiUrl, controller.signal)
            .then(({timeIntervals}) => {
                setFloorsList(timeIntervals);
            })
            .catch((error) => {
                if (error.name !== 'AbortError') {
                    console.error(error);
                }
            });

        floorsAbortController.current = controller;

        return () => floorsAbortController.current?.abort();
    }, []);

    React.useEffect(() => {
        if (!open) {
            setOptions([]);
        }
    }, [open]);

    React.useEffect(() => {
        if (deliveryTime === 0) {
            return;
        }

        const selected = deliveryTimeList.find((item) => item.timeIntervalId === deliveryTime);

        if (!selected || isTimeSlotDisabled(selected)) {
            setDeliveryTime(0);
            setDeliveryTimeLabel('');
        }
    }, [deliveryDate, deliveryTimeList, availabilityByDate]);

    React.useEffect(() => {
        if (!selectedCity) {
            setAvailabilityByDate({});
            return;
        }

        const controller = new AbortController();
        fetchDriverAvailability(apiUrl, selectedCity, controller.signal)
            .then(({days}) => {
                const map: Record<string, AvailabilityDay> = {};
                days.forEach((day) => {
                    map[day.date] = day;
                });
                setAvailabilityByDate(map);
            })
            .catch((error) => {
                if (error.name !== 'AbortError') {
                    console.error(error);
                }
            });

        availabilityAbortController.current = controller;

        return () => availabilityAbortController.current?.abort();
    }, [apiUrl, selectedCity]);

    React.useEffect(() => {
        if (deliveryDate === '' || !dayAvailability) {
            return;
        }

        if (!dayAvailability.available) {
            setDeliveryDate('');
            setDeliveryDateError(false);
        }
    }, [availabilityByDate]);

    React.useEffect(() => {
        const controller = new AbortController();
        // Before a delivery date is chosen, price with "tomorrow" (no today-surcharge)
        // so the price panel is already visible with an accurate base total.
        fetchPrice(apiUrl, selectedCity, selectedSize, deliveryDate || 'tomorrow', floor, bagsCount, controller.signal)
            .then(({finalPrice}) => {
                setFinalPrice(finalPrice);
            })
            .catch((error) => {
                if (error.name !== 'AbortError') {
                    console.error(error);
                }
            });

        priceAbortController.current = controller;

        return () => priceAbortController.current?.abort();
    }, [deliveryDate]);

    return (
        <>
            <div className="clearfix"></div>
            <div id="delivery-section-grid" className="grid">
                <div className="col-70">
                    <div>
                        <div className="pickup-info">
                            <span>Atvykimo detalės</span>
                            <span className="info-badge">Greitas užsakymo planavimas</span>
                        </div>
                        <h3>Kur ir kada atvykti?</h3>
                        <div id="address-section" className={"field-group"}>
                            <label htmlFor="address">Išvežimo adresas</label>

                            <Autocomplete
                                id="address"
                                open={open}
                                onOpen={() => {
                                    setOpen(true);
                                }}
                                onClose={() => {
                                    setOpen(false);
                                }}
                                className={addressError ? 'addressError' : ''}
                                autoComplete
                                includeInputInList
                                filterSelectedOptions
                                value={value}
                                noOptionsText='no address'
                                loadingText='ieskoma adreso'
                                onChange={(event: any, newValue: Address | null) => {
                                    setOptions(newValue ? [newValue, ...options] : options);
                                    setValue(newValue);
                                }}
                                onInputChange={(event, newInputValue) => {
                                    setAddress(newInputValue);
                                    setInputValue(newInputValue);
                                }}
                                filterOptions={(x) => x}
                                getOptionLabel={(option) => option.address}
                                options={options}
                                loading={loading}
                                renderInput={(params) => (
                                    <TextField
                                        placeholder='Gatvė, namo nr., miestas'
                                        {...params}
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <React.Fragment>
                                                    {loading ? <CircularProgress color="inherit" size={20}/> : null}
                                                    {params.InputProps.endAdornment}
                                                </React.Fragment>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        </div>

                        <div id="date-selection-section">
                            <p>Kada atvykti?</p>
                            <div className="schedule-grid" role="radiogroup" aria-label="Kada atvykti?">
                                <div className={(deliveryDate === "today" ? "active" : "") + (isTodayDisabled ? " disabled" : "")}
                                     onClick={isTodayDisabled ? undefined : handleDateSelection}
                                     onKeyDown={isTodayDisabled ? undefined : handleDateSelectionKeyDown}
                                     role="radio" aria-checked={deliveryDate === "today"}
                                     aria-disabled={isTodayDisabled} tabIndex={isTodayDisabled ? -1 : 0}
                                     data-value={"today"} style={deliveryDateError ? {borderColor: "red"} : {}}>
                                    <div className="schedule-top">
                                        <div className="schedule-icon">⚡</div>
                                        <div className="schedule-badge yellow">+20 €</div>
                                    </div>
                                    <p>Šiandien</p>
                                    <small>{isTodayDisabled ? "Nėra laisvų vairuotojų" : "Liko 2 laikai"}</small>
                                </div>
                                <div className={(deliveryDate === "tomorrow" ? "active" : "") + (isTomorrowDisabled ? " disabled" : "")}
                                     onClick={isTomorrowDisabled ? undefined : handleDateSelection}
                                     onKeyDown={isTomorrowDisabled ? undefined : handleDateSelectionKeyDown}
                                     role="radio" aria-checked={deliveryDate === "tomorrow"}
                                     aria-disabled={isTomorrowDisabled} tabIndex={isTomorrowDisabled ? -1 : 0}
                                     data-value={"tomorrow"} style={deliveryDateError ? {borderColor: "red"} : {}}>
                                    <div className="schedule-top">
                                        <div className="schedule-icon">🚀</div>
                                        <div className="schedule-badge green">Populiaru</div>
                                    </div>
                                    <p>Rytoj</p>
                                    <small>{isTomorrowDisabled ? "Nėra laisvų vairuotojų" : "Greitas pasirinkimas"}</small>
                                </div>
                            </div>
                            <div className="date-selection-input"
                                 onClick={() => {
                                     // Guard against the click that closes the calendar (a day
                                     // selection) bubbling here through the React tree and
                                     // immediately reopening it — only open when we know, from
                                     // this render, that it isn't already open.
                                     if (!datePickerOpen) {
                                         setDatePickerOpen(true);
                                     }
                                 }}
                                 style={deliveryDateError ? {borderColor: "red"} : {}}>
                                <div className="choice-description">
                                    <div className="choice-icon">🗓️</div>
                                    <div>
                                        <div className="choice-title">Pasirinkti datą</div>
                                        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={lt}>
                                            <MobileDatePicker
                                                value={customDateValue}
                                                onChange={handleCustomDateChange}
                                                open={datePickerOpen}
                                                onOpen={() => setDatePickerOpen(true)}
                                                onClose={() => setDatePickerOpen(false)}
                                                closeOnSelect
                                                disablePast
                                                shouldDisableDate={(date: Date) => {
                                                    const entry = availabilityByDate[format(date, 'yyyy-MM-dd')];
                                                    return entry ? !entry.available : false;
                                                }}
                                                format="yyyy-MM-dd"
                                                slotProps={{
                                                    textField: {
                                                        variant: 'standard',
                                                        placeholder: 'Pasirinkti datą',
                                                        className: 'choice-sub date-picker-field',
                                                        InputProps: {disableUnderline: true, readOnly: true},
                                                    },
                                                    actionBar: {
                                                        actions: [],
                                                    },
                                                    toolbar: {
                                                        hidden: true,
                                                    },
                                                }}
                                            />
                                        </LocalizationProvider>
                                    </div>
                                </div>
                                <span className="choose-date">Pasirinkti →</span>
                            </div>
                        </div>
                        <p>Pageidaujamas laikas</p>
                        {noSlotsAvailable && (
                            <p className="notice-text">
                                {deliveryDate === 'today'
                                    ? 'Šiandien nebeliko laisvų laikų, pasirinkite kitą dieną.'
                                    : 'Pasirinktai dienai nėra laisvų vairuotojų, pasirinkite kitą dieną.'}
                            </p>
                        )}
                        <ul className={"choices-list"}>
                            {deliveryTimeList.map(function (item) {
                                    const disabled = isTimeSlotDisabled(item);
                                    return <li key={item.timeIntervalId}
                                               className={(deliveryTime === item.timeIntervalId ? "active" : "") + (disabled ? " disabled" : "")}
                                               aria-disabled={disabled}
                                               style={deliveryTimeError ? {borderColor: "red"} : {}}>
                                        <label htmlFor={"ra1" + item.timeIntervalId} aria-label={item.label}></label>
                                        <div className="choice-description">
                                            <div className="choice-icon"
                                                 dangerouslySetInnerHTML={{__html: item.icon}}/>
                                            <div>
                                                <div className="choice-title">{item.label}</div>
                                                <div className="choice-sub">{item.timeInterval}</div>
                                            </div>
                                        </div>
                                        <div className="choice-price-block">
                                            <input type="radio" name="height"
                                                   onClick={disabled ? undefined : handleTimeSelection}
                                                   disabled={disabled}
                                                   value={item.timeIntervalId}
                                                   checked={deliveryTime === item.timeIntervalId}
                                                   id={"ra1" + item.timeIntervalId}/>
                                            <div className="radio"></div>
                                        </div>
                                    </li>
                                }
                            )}
                        </ul>
                        <p>Papildoma informacija (nebūtina)</p>
                        <textarea
                            placeholder="Pvz.: laiptinės kodas, speciali prieiga, kur tiksliai sudėtos atliekos..."
                            onChange={handleNotesFill}>{notes}</textarea>
                    </div>
                </div>

                {finalPrice !== null && (
                    <div className="col-30">
                        <div>
                            <h3 className="price-panel-title">Kaina</h3>
                            <div className="card">
                                <div className="stats">
                                    <div className="stat">
                                        <div className="label">Atliekų kaina</div>
                                        <div className="stat-value ready">{finalPrice.garbagePackagePrice}€</div>
                                    </div>
                                    {floor !== 0 && selectedFloor && (
                                        <div className="stat">
                                            <div className="label">Pasirinktas aukštas</div>
                                            <div className="stat-value ready">{selectedFloor.label}</div>
                                        </div>
                                    )}
                                    {finalPrice.floorPrice > 0 && (
                                        <div className="stat">
                                            <div className="label">Aukšto / lifto kaina</div>
                                            <div className="stat-value ready">+{finalPrice.floorPrice}€</div>
                                        </div>
                                    )}
                                    {finalPrice.deliveryTodayPrice > 0 && (
                                        <div className="stat">
                                            <div className="label">Pristatymas šiandien</div>
                                            <div className="stat-value ready">+{finalPrice.deliveryTodayPrice}€</div>
                                        </div>
                                    )}
                                    {finalPrice.bagsPrice > 0 && (
                                        <div className="stat">
                                            <div className="label">Maišų kaina</div>
                                            <div className="stat-value ready">+{finalPrice.bagsPrice}€</div>
                                        </div>
                                    )}
                                    {floor > 1 && (
                                        <div className="stat">
                                            <div className="label">Liftas</div>
                                            <div className="stat-value ready">{elevatorExist ? "Taip" : "Ne"}</div>
                                        </div>
                                    )}
                                    {floor === 1 && (
                                        <div className="stat">
                                            <div className="label">Atliekos prie namo</div>
                                            <div className="stat-value ready">{trashNearHouse ? "Taip" : "Ne"}</div>
                                        </div>
                                    )}
                                </div>
                                <div className="notice">
                                    <div className="notice-text">
                                        <strong>Bendra preliminari kaina</strong>: <span
                                        key={finalPrice.totalPrice}
                                        className="stat-value green ready price-pulse"
                                        id="totalPrice">{finalPrice.totalPrice}€</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <div className="mobile-sticky-footer">
                {finalPrice !== null && (
                    <div className="mobile-total-price-box">
                        <span>Bendra preliminari kaina</span>
                        <strong key={finalPrice.totalPrice} className="price-pulse">{finalPrice.totalPrice}€</strong>
                    </div>
                )}
                <div className="navigation-block">
                    <button className="btn btn-secondary btn-normal btn-left" onClick={handleBackClick}>← Atgal
                    </button>
                    <button className="btn primary-btn btn-normal btn-right" onClick={handleNextClick}>Tęsti →
                    </button>
                </div>
            </div>
            <NotPossibleToDeliver showNotPossibleToDeliver={showNotPossibleToDeliver}
                                  setShowNotPossibleToDeliver={setShowNotPossibleToDeliver}/>
        </>
    );
}
